import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LoadingSpinner } from "./LoadingSpinner";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";
import { PenLine } from "lucide-react";

interface WriteFoodModalProps {
  open: boolean;
  onClose: () => void;
  onAnalysisComplete: (detectedFoods: any[], rawResponse: string) => void;
}

export const WriteFoodModal = ({ open, onClose, onAnalysisComplete }: WriteFoodModalProps) => {
  const { t, i18n } = useTranslation();
  const [description, setDescription] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = async () => {
    const trimmed = description.trim();
    if (!trimmed) {
      toast({ title: t('components.writeFood.emptyTitle'), description: t('components.writeFood.emptyDescription'), variant: "destructive" });
      return;
    }

    try {
      setIsAnalyzing(true);
      Analytics.foodScanStarted();

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t('components.writeFood.loginRequired'));

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-food-text`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            description: trimmed,
            clientLocale: i18n.language,
            client_local_date: new Date().toLocaleDateString('en-CA'),
          }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      const responseData = await response.json().catch(() => ({ error: 'Unknown error' }));

      if (response.status === 429 || responseData.error === 'daily_limit_reached') {
        Analytics.aiDailyLimitReached();
        toast({
          title: t('components.writeFood.aiLimitTitle'),
          description: responseData.message || t('components.writeFood.aiLimitDefault'),
          variant: "destructive"
        });
        return;
      }

      if (!response.ok) {
        throw new Error(responseData.error || t('components.writeFood.serverError', { status: response.status }));
      }

      if (!responseData.detected_foods || responseData.detected_foods.length === 0) {
        Analytics.foodScanFailed('no_foods_detected');
        toast({
          title: t('components.writeFood.noFoodsTitle'),
          description: t('components.writeFood.noFoodsDescription'),
          variant: "destructive"
        });
        return;
      }

      Analytics.foodScanCompleted(responseData.detected_foods.length);
      onAnalysisComplete(responseData.detected_foods, responseData.raw_response);
      handleClose();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        toast({ title: t('components.writeFood.timedOutTitle'), description: t('components.writeFood.timedOutDescription'), variant: "destructive" });
      } else {
        Analytics.foodScanFailed(error instanceof Error ? error.message : 'unknown_error');
        toast({
          title: t('components.writeFood.analysisFailedTitle'),
          description: error instanceof Error ? error.message : t('components.writeFood.analysisFailedDefault'),
          variant: "destructive"
        });
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClose = () => {
    setDescription("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            {t('components.writeFood.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            placeholder={t('components.writeFood.placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={2000}
            disabled={isAnalyzing}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground">
            {t('components.writeFood.hint')}
          </p>

          {isAnalyzing ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <LoadingSpinner size="lg" />
              <p className="text-sm text-muted-foreground">{t('components.writeFood.analyzing')}</p>
            </div>
          ) : (
            <Button onClick={handleAnalyze} className="w-full" disabled={!description.trim()}>
              {t('components.writeFood.analyzeCta')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
